import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BadgeCheck, Minus, Plus, ShoppingCart, Star, X } from "lucide-react";
import { toast } from "sonner";
import { api, money } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const Stars = ({ value, size = 13 }) => (
  <span className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((s) => (
      <Star key={s} size={size} className={s <= Math.round(value) ? "fill-voltaic-amber text-voltaic-amber" : "text-voltaic-line"} />
    ))}
  </span>
);

export default function ProductModal({ product, onClose }) {
  const { addItem, setIsOpen } = useCart();
  const [qty, setQty] = useState(1);
  const [color, setColor] = useState(null);
  const [reviews, setReviews] = useState({ reviews: [], count: 0, average: 0 });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", rating: 5, title: "", text: "" });
  const [submitting, setSubmitting] = useState(false);

  const loadReviews = (id) => {
    api.get(`/reviews/${id}`).then((r) => setReviews(r.data)).catch(() => {});
  };

  useEffect(() => {
    setQty(1);
    setShowForm(false);
    setColor(product?.colors?.[0] || null);
    if (product?.id) loadReviews(product.id);
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [product, onClose]);

  const add = () => {
    addItem({ ...product, id: product.id }, qty);
    toast.success(`${product.name} added to cart`);
    onClose();
    setIsOpen(true);
  };

  const submitReview = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.title.trim() || !form.text.trim()) {
      toast.error("Fill in all review fields");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/reviews", { product_id: product.id, ...form });
      toast.success(data.verified ? "Verified review posted — thanks!" : "Review posted (purchase not verified for that email)");
      setShowForm(false);
      setForm({ name: "", email: "", rating: 5, title: "", text: "" });
      loadReviews(product.id);
    } catch {
      toast.error("Could not post review");
    }
    setSubmitting(false);
  };

  return (
    <AnimatePresence>
      {product && (
        <motion.div
          key="backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="product-modal-backdrop"
          onClick={onClose}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-md sm:p-6"
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            data-testid="product-modal"
            onClick={(e) => e.stopPropagation()}
            className="relative grid max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-3xl border border-voltaic-line bg-voltaic-surface md:grid-cols-2"
          >
            <button
              data-testid="product-modal-close"
              onClick={onClose}
              aria-label="Close"
              className="absolute right-4 top-4 z-10 grid h-10 w-10 place-items-center rounded-full border border-voltaic-line bg-black/60 text-voltaic-muted backdrop-blur-md transition-colors hover:border-voltaic-cyan hover:text-voltaic-cyan"
            >
              <X size={16} />
            </button>

            <div className="relative min-h-[280px] md:min-h-[560px]">
              <img src={product.image} alt={product.name} className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-voltaic-surface/80 via-transparent to-transparent md:bg-gradient-to-r md:from-transparent md:to-voltaic-surface/40" />
              {product.badge && (
                <span className="absolute left-4 top-4 rounded-full bg-voltaic-cyan px-3 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-voltaic-bg">
                  {product.badge}
                </span>
              )}
            </div>

            <div className="flex flex-col p-6 sm:p-8">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-voltaic-cyan">{product.category}</p>
              <h2 data-testid="product-modal-name" className="mt-2 font-display text-2xl font-extrabold sm:text-3xl">
                {product.name}
              </h2>
              <div className="mt-2 flex items-center gap-2 font-mono text-xs text-voltaic-muted">
                <Stars value={reviews.count ? reviews.average : product.rating} />
                {reviews.count ? `${reviews.average} · ${reviews.count} review${reviews.count === 1 ? "" : "s"}` : `${product.rating.toFixed(1)} · ${product.reviews.toLocaleString()} reviews`}
              </div>

              <div className="mt-4 flex items-baseline gap-3">
                <span data-testid="product-modal-price" className="font-display text-3xl font-extrabold text-voltaic-cyan">
                  {money(product.price)}
                </span>
                {product.compare_at && (
                  <span className="text-base text-voltaic-muted line-through">{money(product.compare_at)}</span>
                )}
              </div>

              <p className="mt-4 text-sm leading-relaxed text-voltaic-muted">{product.description}</p>

              {product.colors?.length > 0 && (
                <div className="mt-5">
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-voltaic-muted">Finish</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {product.colors.map((c) => (
                      <button
                        key={c}
                        data-testid={`color-option-${c.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => setColor(c)}
                        className={`rounded-full border px-4 py-1.5 text-xs transition-colors ${
                          color === c
                            ? "border-voltaic-cyan bg-voltaic-cyan/10 text-voltaic-cyan"
                            : "border-voltaic-line text-voltaic-muted hover:border-voltaic-linebright"
                        }`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-voltaic-muted">Tech specs</p>
                <div className="mt-2 divide-y divide-voltaic-line rounded-xl border border-voltaic-line">
                  {Object.entries(product.specs).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between gap-4 px-4 py-2.5">
                      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-voltaic-muted">{k}</span>
                      <span className="text-right text-sm font-medium">{v}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-5">
                <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-voltaic-muted">
                  <span>Stock</span>
                  <span className={product.stock <= 10 ? "text-voltaic-pink" : "text-voltaic-green"}>
                    {product.stock} left
                  </span>
                </div>
                <div className="mt-2 h-1 overflow-hidden rounded-full bg-voltaic-line">
                  <div
                    className={`h-full rounded-full ${product.stock <= 10 ? "bg-voltaic-pink" : "bg-voltaic-green"}`}
                    style={{ width: `${Math.min(100, (product.stock / 60) * 100)}%` }}
                  />
                </div>
              </div>

              <div className="mt-6 flex items-center gap-3">
                <div className="flex items-center rounded-full border border-voltaic-line">
                  <button
                    data-testid="modal-qty-minus"
                    onClick={() => setQty(Math.max(1, qty - 1))}
                    className="grid h-11 w-11 place-items-center text-voltaic-muted hover:text-voltaic-ink"
                    aria-label="Decrease quantity"
                  >
                    <Minus size={14} />
                  </button>
                  <span data-testid="modal-qty-value" className="w-8 text-center font-mono text-sm">{qty}</span>
                  <button
                    data-testid="modal-qty-plus"
                    onClick={() => setQty(Math.min(product.stock, qty + 1))}
                    className="grid h-11 w-11 place-items-center text-voltaic-muted hover:text-voltaic-ink"
                    aria-label="Increase quantity"
                  >
                    <Plus size={14} />
                  </button>
                </div>
                <button
                  data-testid="modal-add-to-cart"
                  onClick={add}
                  className="flex flex-1 items-center justify-center gap-2 rounded-full bg-voltaic-cyan py-3 font-semibold text-voltaic-bg transition-shadow duration-300 hover:glow-cyan"
                >
                  <ShoppingCart size={16} />
                  Add to cart · {money(product.price * qty)}
                </button>
              </div>
              <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-voltaic-muted">
                SKU {product.id.toUpperCase()} · Free shipping over $999
              </p>

              {/* Reviews */}
              <div className="mt-6 border-t border-voltaic-line pt-5" data-testid="product-reviews">
                <div className="flex items-center justify-between">
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-voltaic-muted">
                    Customer reviews {reviews.count > 0 && `· ${reviews.average} avg`}
                  </p>
                  <button
                    data-testid="write-review-button"
                    onClick={() => setShowForm(!showForm)}
                    className="font-mono text-[10px] uppercase tracking-[0.18em] text-voltaic-cyan transition-colors hover:text-voltaic-ink"
                  >
                    {showForm ? "Cancel" : "Write a review"}
                  </button>
                </div>

                {showForm && (
                  <div className="mt-4 space-y-2.5 rounded-2xl border border-voltaic-line bg-voltaic-bg/50 p-4" data-testid="review-form">
                    <div className="grid grid-cols-2 gap-2.5">
                      <input data-testid="review-name-input" className="field !py-2 text-sm" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                      <input data-testid="review-email-input" type="email" className="field !py-2 text-sm" placeholder="Order email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                    </div>
                    <div className="flex items-center gap-1.5" data-testid="review-rating-select">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <button key={s} data-testid={`review-star-${s}`} onClick={() => setForm({ ...form, rating: s })} aria-label={`${s} stars`}>
                          <Star size={18} className={s <= form.rating ? "fill-voltaic-amber text-voltaic-amber" : "text-voltaic-line"} />
                        </button>
                      ))}
                    </div>
                    <input data-testid="review-title-input" className="field !py-2 text-sm" placeholder="Review headline" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                    <textarea data-testid="review-text-input" className="field !py-2 text-sm" rows={3} placeholder="How is it in real use?" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} />
                    <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-voltaic-muted">We verify reviews against your order email</p>
                    <button data-testid="review-submit-button" onClick={submitReview} disabled={submitting} className="w-full rounded-full bg-voltaic-cyan py-2.5 text-sm font-semibold text-voltaic-bg disabled:opacity-50">
                      {submitting ? "Posting..." : "Post review"}
                    </button>
                  </div>
                )}

                <div className="mt-4 space-y-3">
                  {reviews.reviews.length === 0 && !showForm && (
                    <p className="text-xs text-voltaic-muted">No reviews yet — be the first after your order lands.</p>
                  )}
                  {reviews.reviews.slice(0, 4).map((r, i) => (
                    <div key={i} data-testid={`review-item-${i}`} className="rounded-xl border border-voltaic-line/60 p-3.5">
                      <div className="flex items-center justify-between gap-2">
                        <Stars value={r.rating} size={11} />
                        {r.verified && (
                          <span className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.12em] text-voltaic-green">
                            <BadgeCheck size={11} /> Verified buyer
                          </span>
                        )}
                      </div>
                      <p className="mt-1.5 text-sm font-semibold">{r.title}</p>
                      <p className="mt-0.5 text-xs leading-relaxed text-voltaic-muted">{r.text}</p>
                      <p className="mt-1.5 font-mono text-[9px] uppercase tracking-[0.15em] text-voltaic-muted">{r.name}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
